// Upload widget (guests: Gästemedien; admin: any category). One flow for photos
// and videos: pick/drop → hash locally → ask the server which already exist →
// upload only the new ones, 3 at a time, with byte-weighted aggregate progress.
// Ends in the required German summary ("87 Dateien erfolgreich hochgeladen.
// 5 Duplikate wurden übersprungen.").
import React, { useEffect, useRef, useState } from 'react';
import { api, getLimits, uploadFile } from '../api.js';
import { sha256File } from '../hash.js';
import { ERRORS, UPLOAD, fmtBytes, uploadSummary } from '../text.js';
import { CameraIcon, FilmIcon, PlusIcon } from './icons.jsx';
import { toast } from './toast.jsx';

const CONCURRENCY = 3;
// Above this size a file gets the uplink to itself. Three big videos in parallel
// share one phone connection, so each crawls and all three are at risk at once.
const LARGE_FILE = 64 * 1024 * 1024;
// Party Wi-Fi drops connections mid-body; the server sees a truncated request and
// answers 400 no_file, Apache turns a dead upstream socket into a 502. Both are
// worth starting over, and once is not enough.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const RETRYABLE_CODES = new Set(['network', 'no_file', 'incomplete_upload', 'timeout']);

const isMedia = (file) => /^(image|video)\//.test(file.type) || /\.(jpe?g|png|gif|webp|heic|heif|mp4|mov|m4v|webm|avi|3gp)$/i.test(file.name);
const isVideo = (file) => /^video\//.test(file.type) || /\.(mp4|mov|m4v|webm|avi|3gp)$/i.test(file.name);
const maxBytesFor = (file) => (isVideo(file) ? getLimits().videoBytes : getLimits().imageBytes);
const isRetryable = (err) => RETRYABLE_CODES.has(err.code) || err.status === 502 || err.status === 503 || err.status === 504;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function Uploader({ category, onUploaded, title }) {
	const inputRef = useRef(null);
	const cancelRef = useRef(null);
	const [dragOver, setDragOver] = useState(false);
	const [phase, setPhase] = useState('idle'); // idle | hashing | uploading | done
	const [progress, setProgress] = useState({ done: 0, total: 0, pct: 0 });
	const [summary, setSummary] = useState(null); // {uploaded, duplicates, failed}
	const [failedFiles, setFailedFiles] = useState([]);
	// Touch devices get picker-focused wording — drag & drop means nothing there.
	const [isTouch, setIsTouch] = useState(false);

	useEffect(() => {
		setIsTouch(window.matchMedia('(pointer: coarse)').matches);
	}, []);

	// Don't let a guest wander off mid-upload without a nudge.
	const active = phase === 'hashing' || phase === 'uploading';
	useEffect(() => {
		if (!active) return;
		const guard = (e) => {
			e.preventDefault();
			e.returnValue = UPLOAD.leaveGuard;
		};
		window.addEventListener('beforeunload', guard);
		return () => window.removeEventListener('beforeunload', guard);
	}, [active]);

	const run = async (fileList) => {
		const picked = Array.from(fileList || []).filter(isMedia);
		if (!picked.length || active) return;

		const cancel = { aborted: false, controller: new AbortController() };
		cancelRef.current = cancel;
		setSummary(null);
		setFailedFiles([]);

		// 0) Turn away what the server would reject anyway. Doing this here instead of
		// after the transfer is the whole point: the old flow pushed the entire file
		// over a phone uplink and only then answered 413.
		const files = [];
		const oversize = [];
		for (const file of picked) {
			if (file.size > maxBytesFor(file)) oversize.push(file);
			else files.push(file);
		}
		oversize.forEach((file) => toast(UPLOAD.tooLarge(file.name, fmtBytes(maxBytesFor(file))), 'error'));
		if (!files.length) {
			setPhase('done');
			setSummary({ uploaded: 0, duplicates: 0, failed: 0, tooLarge: oversize.length });
			return;
		}

		// 1) Hash everything locally (sequential — the WASM hasher is shared). Progress
		// is byte-weighted so a single large video doesn't sit at 0/1 for minutes.
		setPhase('hashing');
		const hashBytesTotal = files.reduce((sum, f) => sum + f.size, 0) || 1;
		let hashBytesDone = 0;
		setProgress({ done: 0, total: files.length, pct: 0 });
		const hashed = [];
		const seen = new Set(); // duplicates *within* the selection
		let duplicates = 0;
		for (const [i, file] of files.entries()) {
			if (cancel.aborted) return;
			const base = hashBytesDone;
			try {
				const hash = await sha256File(file, (bytes) => {
					setProgress({ done: i, total: files.length, pct: Math.round(((base + bytes) / hashBytesTotal) * 100) });
				});
				if (seen.has(hash)) {
					duplicates += 1;
				} else {
					seen.add(hash);
					hashed.push({ file, hash });
				}
			} catch {
				// Hashing failed (rare) — upload anyway, the server enforces dedupe.
				hashed.push({ file, hash: null });
			}
			hashBytesDone = base + file.size;
			setProgress({ done: i + 1, total: files.length, pct: Math.round((hashBytesDone / hashBytesTotal) * 100) });
		}

		// 2) Ask which hashes the server already has; skip those without uploading.
		let known = new Set();
		try {
			const allHashes = hashed.filter((h) => h.hash).map((h) => h.hash);
			for (let i = 0; i < allHashes.length; i += 500) {
				const res = await api.checkHashes(category, allHashes.slice(i, i + 500));
				res.duplicates.forEach((h) => known.add(h));
			}
		} catch {
			known = new Set(); // pre-check is an optimization — the upload path enforces
		}
		const toUpload = hashed.filter((h) => !h.hash || !known.has(h.hash));
		duplicates += hashed.length - toUpload.length;

		// 3) Upload: small files CONCURRENCY at a time for quick wins, then the big
		// videos strictly one at a time so each owns the uplink. Byte-weighted
		// progress spans both passes.
		if (cancel.aborted) return;
		setPhase('uploading');
		const bytesTotal = toUpload.reduce((sum, h) => sum + h.file.size, 0) || 1;
		const loaded = new Array(toUpload.length).fill(0);
		let uploaded = 0;
		let filesDone = 0;
		let soloIndex = -1; // the file that currently has the uplink to itself
		let retrying = false;
		const failed = [];
		const records = [];

		const bumpProgress = () => {
			const bytesDone = loaded.reduce((a, b) => a + b, 0);
			const solo = soloIndex >= 0 ? Math.min(100, Math.round((loaded[soloIndex] / (toUpload[soloIndex].file.size || 1)) * 100)) : null;
			setProgress({
				done: filesDone,
				total: toUpload.length,
				pct: Math.min(100, Math.round((bytesDone / bytesTotal) * 100)),
				videoPct: solo,
				retrying,
			});
		};

		const uploadOne = async (entry, i) => {
			for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
				try {
					const res = await uploadFile(entry.file, category, {
						signal: cancel.controller.signal,
						onProgress: (l) => {
							loaded[i] = l;
							bumpProgress();
						},
					});
					loaded[i] = entry.file.size;
					if (res.duplicate) duplicates += 1;
					else {
						uploaded += 1;
						records.push(res);
					}
					return;
				} catch (err) {
					if (cancel.aborted) return;
					if (err.code === 'disk_full') {
						toast(ERRORS.diskFull, 'error');
						failed.push(entry.file);
						return;
					}
					if (err.code === 'too_large') {
						// Only reachable when the client-side cap and the server's disagree.
						toast(UPLOAD.tooLarge(entry.file.name, fmtBytes(maxBytesFor(entry.file))), 'error');
						oversize.push(entry.file);
						return;
					}
					if (attempt === MAX_ATTEMPTS || !isRetryable(err)) {
						failed.push(entry.file);
						return;
					}
					// A truncated body left nothing usable on the server — start over,
					// backing off a little further each time.
					loaded[i] = 0;
					retrying = true;
					bumpProgress();
					await sleep(RETRY_DELAY_MS * attempt);
					retrying = false;
				}
			}
		};

		const runFile = async (entry, i) => {
			await uploadOne(entry, i);
			filesDone += 1;
			bumpProgress();
		};

		const indexed = toUpload.map((entry, i) => ({ entry, i }));
		const small = indexed.filter((it) => it.entry.file.size < LARGE_FILE);
		const large = indexed.filter((it) => it.entry.file.size >= LARGE_FILE);

		let next = 0;
		await Promise.all(
			Array.from({ length: CONCURRENCY }, async () => {
				while (next < small.length && !cancel.aborted) {
					const it = small[next];
					next += 1;
					await runFile(it.entry, it.i);
				}
			}),
		);
		for (const it of large) {
			if (cancel.aborted) return;
			soloIndex = it.i;
			await runFile(it.entry, it.i);
		}
		soloIndex = -1;

		if (cancel.aborted) return;
		setPhase('done');
		setSummary({ uploaded, duplicates, failed: failed.length, tooLarge: oversize.length });
		setFailedFiles(failed);
		if (records.length) onUploaded?.(records);
	};

	const cancelRun = () => {
		if (cancelRef.current) {
			cancelRef.current.aborted = true;
			cancelRef.current.controller.abort();
		}
		setPhase('idle');
	};

	const onPick = (e) => {
		run(e.target.files);
		e.target.value = ''; // allow re-selecting the same files
	};
	const openPicker = () => inputRef.current?.click();

	const statusLabel = () => {
		if (phase === 'hashing') return UPLOAD.hashing(progress.done, progress.total);
		// A big video runs alone and for minutes — name its own percentage, or the
		// aggregate bar looks stuck.
		if (progress.videoPct != null) return UPLOAD.uploadingVideo(progress.done + 1, progress.total, progress.videoPct);
		return UPLOAD.uploading(progress.done, progress.total);
	};

	return (
		<section className='upl' aria-label={title ?? UPLOAD.title}>
			{title && <h2 className='upl__title'>{title}</h2>}

			<div
				className={`upl__zone ${dragOver ? 'upl__zone--over' : ''} ${active ? 'upl__zone--busy' : ''}`}
				role='button'
				tabIndex={0}
				aria-label='Fotos und Videos auswählen'
				onClick={() => !active && openPicker()}
				onKeyDown={(e) => {
					if ((e.key === 'Enter' || e.key === ' ') && !active) {
						e.preventDefault();
						openPicker();
					}
				}}
				onDragOver={(e) => {
					e.preventDefault();
					setDragOver(true);
				}}
				onDragLeave={() => setDragOver(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragOver(false);
					if (!active) run(e.dataTransfer.files);
				}}>
				<div className='upl__icons' aria-hidden='true'>
					<span className='upl__icon'>
						<CameraIcon size={26} />
					</span>
					<span className='upl__icon'>
						<FilmIcon size={24} />
					</span>
					<span className='upl__icon upl__icon--plus'>
						<PlusIcon size={18} />
					</span>
				</div>
				<div className='upl__zone-title'>{UPLOAD.zoneTitle}</div>
				<div className='upl__zone-sub'>{isTouch ? UPLOAD.zoneSubTouch : UPLOAD.zoneSubMouse}</div>

				{!active && (
					<div className='upl__buttons'>
						<button
							type='button'
							className='btn btn--primary'
							onClick={(e) => {
								e.stopPropagation();
								openPicker();
							}}>
							{UPLOAD.pick}
						</button>
					</div>
				)}

				{active && (
					<div className='upl__status' onClick={(e) => e.stopPropagation()}>
						<div className='upl__status-label' role='status'>
							{statusLabel()}
						</div>
						<div className='upl__progress' aria-hidden='true'>
							<div className='upl__progress-fill' style={{ width: `${progress.pct}%` }} />
						</div>
						{progress.retrying && <div className='upl__status-note'>{UPLOAD.retrying}</div>}
						{progress.videoPct != null && !progress.retrying && <div className='upl__status-note'>{UPLOAD.slowHint}</div>}
						<button type='button' className='btn btn--ghost upl__cancel' onClick={cancelRun}>
							Abbrechen
						</button>
					</div>
				)}

				{phase === 'done' && summary && (
					<div className='upl__summary' onClick={(e) => e.stopPropagation()} role='status'>
						<div className={`upl__msg ${summary.failed || summary.tooLarge ? 'upl__msg--warn' : 'upl__msg--ok'}`}>
							{uploadSummary(summary.uploaded, summary.duplicates, summary.failed, summary.tooLarge)}
						</div>
						{failedFiles.length > 0 && (
							<button type='button' className='btn upl__retry' onClick={() => run(failedFiles)}>
								{UPLOAD.retryFailed(failedFiles.length)}
							</button>
						)}
					</div>
				)}
			</div>

			{/* No capture attribute on purpose: guests share existing photos, they don't take new ones here. */}
			<input ref={inputRef} type='file' accept='image/*,video/*' multiple onChange={onPick} hidden />
		</section>
	);
}
